import {
  Button,
  Flex,
  HStack,
  Input,
  Modal,
  Progress,
  Select,
  Spinner,
  Text,
  Textarea,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useState } from "react";
import { MdCheckCircle, MdClose } from "react-icons/md";
import serverGenerationHubService, {
  ServerProgressUpdate,
} from "services/serverGenerationHubService";
import {
  ApiDocType,
  CreateServerRequest,
  serverService,
} from "services/serverService";

interface CreateServerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onServerCreated: (serverId?: string) => void;
}

const CreateServerModal = ({
  isOpen,
  onClose,
  onServerCreated,
}: CreateServerModalProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [apiDocType, setApiDocType] = useState<ApiDocType>(ApiDocType.Swagger);
  const [specUrl, setSpecUrl] = useState("");

  // Validation state
  const [validated, setValidated] = useState(false);
  const [serverName, setServerName] = useState("");
  const [serverDescription, setServerDescription] = useState("");
  const [additionalDetailsUrl, setAdditionalDetailsUrl] = useState("");

  // Track if API doc fields changed after validation
  const [validatedApiDocType, setValidatedApiDocType] =
    useState<ApiDocType | null>(null);
  const [validatedSpecUrl, setValidatedSpecUrl] = useState<string | null>(null);
  // True when the entered URL was a documentation page and we swapped in the real spec URL
  const [specUrlWasResolved, setSpecUrlWasResolved] = useState(false);

  // Generation progress state
  const [generating, setGenerating] = useState(false);
  const [generatedServerId, setGeneratedServerId] = useState<string | null>(
    null,
  );
  const [generationProgress, setGenerationProgress] = useState(0);
  const [processedTools, setProcessedTools] = useState(0);
  const [totalTools, setTotalTools] = useState(0);
  const [generationMessage, setGenerationMessage] = useState("");
  const [generationComplete, setGenerationComplete] = useState(false);

  // Handle API doc type change - reset validation
  const handleApiDocTypeChange = (newType: ApiDocType) => {
    setApiDocType(newType);
    if (validated) {
      setValidated(false);

      setServerName("");
      setServerDescription("");
      setValidatedApiDocType(null);
      setValidatedSpecUrl(null);
      setSpecUrlWasResolved(false);
    }
  };

  // Handle spec URL change - reset validation
  const handleSpecUrlChange = (newUrl: string) => {
    setSpecUrl(newUrl);
    if (validated) {
      setValidated(false);

      setServerName("");
      setServerDescription("");
      setValidatedApiDocType(null);
      setValidatedSpecUrl(null);
      setSpecUrlWasResolved(false);
    }
  };

  const handleValidate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!specUrl) {
      setError("API Documentation URL is required");
      return;
    }

    setLoading(true);
    try {
      const response = await serverService.validateSpec({
        specUrl,
        apiDocType,
      });

      if (response.isValid) {
        // The user may have pasted the documentation page rather than the spec itself. The API
        // resolves the real spec URL in that case — show it, so what we create matches what they see.
        const effectiveSpecUrl =
          response.specUrlWasResolved && response.resolvedSpecUrl
            ? response.resolvedSpecUrl
            : specUrl;

        if (effectiveSpecUrl !== specUrl) {
          setSpecUrl(effectiveSpecUrl);
        }

        setServerName(response.serverName || "");
        setServerDescription(response.serverDescription || "");
        setValidated(true);
        setValidatedApiDocType(apiDocType);
        setValidatedSpecUrl(effectiveSpecUrl);
        setSpecUrlWasResolved(response.specUrlWasResolved === true);
      } else {
        setError(
          response.message || t("app.servers.createModal.validationFailed"),
        );
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          t("app.servers.createModal.validateSpecFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!serverName || !serverDescription || !specUrl) {
      setError(t("app.servers.createModal.nameDescriptionSpecRequired"));
      return;
    }

    const requestData: CreateServerRequest = {
      name: serverName,
      description: serverDescription,
      specUrl,
      additionalDetailsUrl: additionalDetailsUrl || undefined,
      apiDocType,
      autoGeneratePrompts: false,
    };

    setLoading(true);
    try {
      const response = await serverService.createServer(requestData);

      setGeneratedServerId(response.id);
      setGenerating(true);
      onServerCreated(response.id);

      serverGenerationHubService.notifyGenerationStarted(response.id);

      const handleProgress = (update: ServerProgressUpdate) => {
        setGenerationProgress(update.progress);
        setProcessedTools(update.processedTools);
        setTotalTools(update.totalTools);
        setGenerationMessage(update.message ?? "");
        if (update.progress >= 100) {
          setGenerationComplete(true);
          serverGenerationHubService.unsubscribeFromServer(
            response.id,
            handleProgress,
          );
          setTimeout(() => handleClose(), 2000);
        }
      };

      serverGenerationHubService.subscribeToServer(response.id, handleProgress);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          t("app.servers.createModal.createFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (generatedServerId) {
      serverGenerationHubService.unsubscribeFromServer(generatedServerId);
    }
    setError("");
    setValidated(false);
    // validation cleared
    setServerName("");
    setServerDescription("");
    setSpecUrl("");
    setAdditionalDetailsUrl("");
    setApiDocType(ApiDocType.Swagger);
    setValidatedApiDocType(null);
    setValidatedSpecUrl(null);
    setSpecUrlWasResolved(false);
    setGenerating(false);
    setGeneratedServerId(null);
    setGenerationProgress(0);
    setProcessedTools(0);
    setTotalTools(0);
    setGenerationMessage("");
    setGenerationComplete(false);
    onClose();
  };

  // Check if API doc fields changed after validation
  const apiDocFieldsChanged =
    validated &&
    (apiDocType !== validatedApiDocType || specUrl !== validatedSpecUrl);

  const buttonText = loading
    ? validated
      ? "Creating Server..."
      : "Validating..."
    : validated && !apiDocFieldsChanged
      ? "Create Server"
      : "Validate";

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>
        <Flex justify="space-between" align="center">
          <h3
            style={{
              fontSize: "1.5rem",
              fontWeight: "bold",
              color: "var(--modal-header-text)",
            }}
          >
            {t("app.servers.createModal.title")}
          </h3>
          <Modal.CloseTrigger asChild>
            <button
              style={{
                color: "var(--modal-close-icon)",
                transition: "color 0.2s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.color = "var(--modal-close-icon-hover)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.color = "var(--modal-close-icon)")
              }
            >
              <MdClose style={{ height: "1.5rem", width: "1.5rem" }} />
            </button>
          </Modal.CloseTrigger>
        </Flex>
      </Modal.Header>

      <Modal.Body>
        {generating ? (
          <Flex direction="column" align="center" gap={5} py={4}>
            {generationComplete ? (
              <MdCheckCircle
                style={{
                  color: "var(--chakra-colors-green-500)",
                  fontSize: "2.5rem",
                }}
              />
            ) : (
              <Spinner size="xl" />
            )}

            <Text fontWeight="bold" fontSize="lg" textAlign="center">
              {generationComplete
                ? t("app.servers.createModal.generationComplete")
                : t("app.servers.createModal.generatingTools")}
            </Text>

            <Progress
              value={generationProgress}
              max={100}
              w="100%"
              colorPalette={generationComplete ? "green" : "blue"}
            />

            <Text
              fontSize="sm"
              color="var(--text-secondary)"
              textAlign="center"
            >
              {totalTools > 0
                ? `${processedTools} / ${totalTools} tools`
                : generationMessage ||
                  t("app.servers.createModal.generationStarting")}
            </Text>

            {!generationComplete && (
              <Button
                variant="ghost"
                size="sm"
                colorPalette="gray"
                onClick={handleClose}
              >
                {t("app.servers.createModal.continueInBackground")}
              </Button>
            )}
          </Flex>
        ) : (
          <>
            <p
              style={{
                marginBottom: "1.5rem",
                fontSize: "0.875rem",
                color: "var(--text-secondary)",
              }}
            >
              {validated
                ? "Review and edit the server details parsed from your API specification."
                : "Provide your API documentation URL to validate and extract server information."}
            </p>

            {error && (
              <div
                style={{
                  marginBottom: "1rem",
                  borderRadius: "0.5rem",
                  padding: "0.75rem",
                  fontSize: "0.875rem",
                  backgroundColor: "var(--alert-error-bg)",
                  color: "var(--alert-error-text)",
                }}
              >
                {error}
              </div>
            )}

            <form
              onSubmit={
                validated && !apiDocFieldsChanged
                  ? handleCreate
                  : handleValidate
              }
              id="create-server-form"
            >
              <Flex direction="column" gap={4}>
                <Select
                  label={t("app.servers.createModal.apiDocTypeLabel")}
                  value={[apiDocType.toString()]}
                  onValueChange={(details: any) =>
                    handleApiDocTypeChange(
                      parseInt(details.value[0]) as ApiDocType,
                    )
                  }
                  items={[
                    {
                      label: t("app.servers.createModal.swaggerOption"),
                      value: ApiDocType.Swagger.toString(),
                    },
                    {
                      label: t("app.servers.createModal.readmeOption"),
                      value: ApiDocType.Readme.toString(),
                    },
                  ]}
                  helperText={t("app.servers.createModal.apiDocTypeHelper")}
                  required
                />

                <Input
                  label={t("app.servers.createModal.specUrlLabel")}
                  type="url"
                  value={specUrl}
                  onChange={(e: any) => handleSpecUrlChange(e.target.value)}
                  placeholder={
                    apiDocType === ApiDocType.Swagger
                      ? t("app.servers.createModal.specUrlPlaceholder")
                      : t("app.servers.createModal.readmeSpecUrlPlaceholder")
                  }
                  helperText={
                    apiDocType === ApiDocType.Swagger
                      ? t("app.servers.createModal.specUrlHelperSwagger")
                      : t("app.servers.createModal.specUrlHelperReadme")
                  }
                  required
                />

                {/* Shown when the user pasted a documentation page and we swapped in the real spec URL. */}
                {validated && specUrlWasResolved && (
                  <Text fontSize="xs" color="var(--text-secondary)">
                    {t("app.servers.createModal.specUrlResolved")}
                  </Text>
                )}

                {validated && (
                  <>
                    <Input
                      label={t("app.servers.createModal.nameLabel")}
                      type="text"
                      value={serverName}
                      onChange={(e: any) => setServerName(e.target.value)}
                      placeholder={t("app.servers.createModal.namePlaceholder")}
                      required
                    />

                    <Textarea
                      label={t("app.servers.createModal.descriptionLabel")}
                      value={serverDescription}
                      onChange={(e: any) =>
                        setServerDescription(e.target.value)
                      }
                      placeholder={t(
                        "app.servers.createModal.descriptionPlaceholder",
                      )}
                      rows={3}
                      required
                    />

                    <Input
                      label={t(
                        "app.servers.createModal.additionalDetailsLabel",
                      )}
                      type="url"
                      value={additionalDetailsUrl}
                      onChange={(e: any) =>
                        setAdditionalDetailsUrl(e.target.value)
                      }
                      placeholder={t(
                        "app.servers.createModal.additionalDetailsPlaceholder",
                      )}
                      helperText={t(
                        "app.servers.createModal.additionalDetailsHelper",
                      )}
                    />
                  </>
                )}
              </Flex>
            </form>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {generating ? (
          <Button
            variant="solid"
            colorPalette="blue"
            disabled={!generationComplete}
            onClick={handleClose}
            w="full"
          >
            {t("app.servers.createModal.viewTools")}
          </Button>
        ) : (
          <HStack gap={3}>
            <Button
              type="button"
              onClick={handleClose}
              variant="subtle"
              colorPalette="gray"
              flex={1}
            >
              {t("app.common.cancel")}
            </Button>
            <Button
              type="submit"
              form="create-server-form"
              disabled={loading}
              variant="solid"
              colorPalette="blue"
              flex={1}
            >
              {buttonText}
            </Button>
          </HStack>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default CreateServerModal;
